import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';
import { useDrop, type DropTargetMonitor } from 'react-dnd';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { DROP_TARGET_TYPES, ELEMENT_DND_TYPE } from '../../../../common/elements/elementConstants';
import { getElementId, getTextContent } from '../../../../common/elements/utils/elementPropertyUtils';
import { asObject } from '../../../../common/utils/immutableHelper';
import type { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import {
    copyTaskListItemsToTaskList,
    moveTaskListItemsToTaskList,
} from '../../../../common/tiptap/extensions/taskList/taskListItemDropUtils';
import {
    getDraggedCheckboxX,
    getTaskItemInsertionIndex,
    getTaskListDropIndicator,
    getTaskListDropTarget,
    type TaskListDropIndicator,
} from './taskListDropGeometryUtils';
import {
    doesTaskListAcceptDrag,
    getCopyDropAdjustments,
    isNoOpTaskItemDrop,
    isTaskListElementDrag,
    type TaskItemDrag,
} from './taskListDropAcceptanceUtils';
import { moveElementsToTrash } from '../../actions/elementShortcutActions';
import { getNewTransactionId } from '../../../utils/undoRedo/undoRedoTransactionManager';
import { getCurrentBoardIdFromState } from '../../../reducers/currentBoardId/currentBoardIdSelector';
import { globalGridSizeSelector } from '../../../utils/grid/gridSizeSelector';
import { isCopyDrag } from '../../../utils/dnd/modifierKeys/dragModifierKeysUtils';
import type { XYCoord } from 'dnd-core';
import { isString } from 'lodash';

type TaskListDropResult = {
    taskListDrop?: { elementId: string; insertionPosition: number; reparentFollowingTaskItemsFromPosition?: number };
    taskListElementDrop?: true;
};

/**
 * Determines whether two indicator positions render at the same location.
 */
const isSameDropIndicator = (
    firstIndicator: TaskListDropIndicator | null,
    secondIndicator: TaskListDropIndicator | null,
): boolean => firstIndicator?.top === secondIndicator?.top && firstIndicator?.left === secondIndicator?.left;

/**
 * Registers this task list as a valid list target so the drag preview
 * will go transparent on hover of another list.
 */
const setTaskListHoverType = (item: TaskItemDrag, monitor: DropTargetMonitor): void => {
    const targetId = monitor.getHandlerId();

    if (!item.hoveredTypesRegistry || !isString(targetId)) return;

    item.hoveredTypesRegistry[targetId] = DROP_TARGET_TYPES.LIST;
};

/**
 * Determines whether we have all the info needed to handle a DnD event and that it's a task item or task list drag.
 */
const shouldHandleDndEvent = (
    editor: Editor | null,
    clientOffset: XYCoord | null,
    item: TaskItemDrag,
    elementId: string,
): editor is Editor => {
    if (!editor) return false;
    if (!clientOffset) return false;

    if (item.taskItemDrag) return true;

    return isTaskListElementDrag({ item, targetElementId: elementId });
};

/**
 * Resolves everything both DnD handlers need about the hovered gap: the rendered rows, the
 * insertion index a copy may have snapped out of its own subtree, the chosen drop target, and
 * whether a move there would leave the document unchanged.
 *
 * Shared so the hover indicator and the drop it previews can never disagree.
 */
const resolveTaskListDrop = ({
    editor,
    elementId,
    taskItemDrag,
    isCopy,
    clientY,
    draggedCheckboxX,
}: {
    editor: Editor;
    elementId: string;
    taskItemDrag: TaskItemDrag['taskItemDrag'];
    isCopy: boolean;
    clientY: number;
    draggedCheckboxX: number;
}): {
    rows: HTMLElement[];
    insertionIndex: number;
    dropTarget: ReturnType<typeof getTaskListDropTarget>;
    isNoOpDrop: boolean;
} => {
    const rows = Array.from(editor.view.dom.querySelectorAll<HTMLElement>('.task-item-row'));
    const hoveredInsertionIndex = getTaskItemInsertionIndex({ rows, clientY });

    // A copy can't land inside the original it was taken from, so its gap and its available
    // depths are both adjusted before the drop target is derived from them.
    const { insertionIndex, excludedTaskListAncestor } = isCopy
        ? getCopyDropAdjustments({
              rows,
              insertionIndex: hoveredInsertionIndex,
              taskItemDrag,
              targetElementId: elementId,
              clientY,
          })
        : { insertionIndex: hoveredInsertionIndex, excludedTaskListAncestor: undefined };

    return {
        rows,
        insertionIndex,
        dropTarget: getTaskListDropTarget({ rows, insertionIndex, draggedCheckboxX, excludedTaskListAncestor }),
        // A move back to where the item already is changes nothing, so it renders no indicator
        // and produces no drop result
        isNoOpDrop: !isCopy && isNoOpTaskItemDrop({ rows, insertionIndex, taskItemDrag, targetElementId: elementId }),
    };
};

/**
 * Gets a task item's document position from its rendered row.
 */
const getTaskItemPosition = ({ editor, row }: { editor: Editor; row: HTMLElement | undefined }): number | undefined => {
    const taskItemElement = row?.closest('li');
    const taskListElement = taskItemElement?.parentElement;

    if (!taskItemElement || !taskListElement) return undefined;

    const taskIndex = Array.from(taskListElement.children).indexOf(taskItemElement);

    return taskIndex === -1 ? undefined : editor.view.posAtDOM(taskListElement, taskIndex);
};

/**
 * Connects a V2 task list to task-item drops and exposes the hovered insertion indicator.
 */
const TaskListDropTarget: React.FC<{
    editor: Editor | null;
    elementId: string;
    setParentHoveredChildAcceptsDrop?: (hoveredChildAcceptsDrop: boolean) => void;
    children: (args: {
        connectDropTarget: React.RefCallback<HTMLDivElement>;
        dropIndicator: TaskListDropIndicator | null;
    }) => React.ReactNode;
}> = ({ editor, elementId, setParentHoveredChildAcceptsDrop, children }) => {
    // Stable - won't cause re-renders
    const store = useStore();
    const dispatch = useDispatch();
    const currentBoardId = useSelector(getCurrentBoardIdFromState);
    const gridSize = useSelector(globalGridSizeSelector);
    const [dropIndicator, setDropIndicator] = useState<TaskListDropIndicator | null>(null);
    const dropTargetElementRef = useRef<HTMLDivElement | null>(null);

    /**
     * Gets the viewport X of the dragged item's / dragged list's first checkbox, which
     * determines the drop depth (rather than the pointer position).
     */
    const getMonitorDraggedCheckboxX = (item: TaskItemDrag, monitor: DropTargetMonitor): number =>
        getDraggedCheckboxX({
            clientX: monitor.getClientOffset()!.x,
            sourceClientX: monitor.getSourceClientOffset()?.x,
            scaledCustomDragOffsetX: item.scaledCustomDragOffset?.x ?? 0,
            dropTargetElement: dropTargetElementRef.current,
            gridSize,
        });

    const [{ isOver, canDrop }, connectDropTarget] = useDrop<
        TaskItemDrag,
        TaskListDropResult,
        { isOver: boolean; canDrop: boolean }
    >({
        accept: ELEMENT_DND_TYPE,
        canDrop: (item) => doesTaskListAcceptDrag({ item, editor, currentBoardId, targetElementId: elementId }),
        hover: (item, monitor) => {
            const clientOffset = monitor.getClientOffset();

            if (!shouldHandleDndEvent(editor, clientOffset, item, elementId)) {
                setDropIndicator(null);
                return;
            }

            const {
                rows,
                insertionIndex: updatedInsertionIndex,
                dropTarget: updatedDropTarget,
                isNoOpDrop,
            } = resolveTaskListDrop({
                editor,
                elementId,
                taskItemDrag: item.taskItemDrag,
                isCopy: isCopyDrag(store.getState()),
                clientY: clientOffset!.y,
                draggedCheckboxX: getMonitorDraggedCheckboxX(item, monitor),
            });

            if (isNoOpDrop) {
                setDropIndicator(null);
                return;
            }

            setTaskListHoverType(item, monitor);

            const updatedDropIndicator =
                updatedDropTarget &&
                getTaskListDropIndicator({
                    rows,
                    insertionIndex: updatedInsertionIndex,
                    dropTargetElement: dropTargetElementRef.current,
                    taskListDropTarget: updatedDropTarget,
                });

            setDropIndicator((currentDropIndicator) =>
                isSameDropIndicator(currentDropIndicator, updatedDropIndicator)
                    ? currentDropIndicator
                    : updatedDropIndicator,
            );
        },
        drop: (item, monitor) => {
            const clientOffset = monitor.getClientOffset();

            if (!shouldHandleDndEvent(editor, clientOffset, item, elementId)) return undefined;

            const isCopy = isCopyDrag(store.getState());
            const { rows, insertionIndex, dropTarget, isNoOpDrop } = resolveTaskListDrop({
                editor,
                elementId,
                taskItemDrag: item.taskItemDrag,
                isCopy,
                clientY: clientOffset!.y,
                draggedCheckboxX: getMonitorDraggedCheckboxX(item, monitor),
            });

            if (isNoOpDrop) return undefined;
            if (!dropTarget) return undefined;

            const insertionPosition = editor.view.posAtDOM(dropTarget.taskListElement, dropTarget.taskIndex);

            if (item.taskItemDrag) {
                return {
                    taskListDrop: {
                        elementId,
                        insertionPosition,
                        reparentFollowingTaskItemsFromPosition: dropTarget.reparentsFollowingTaskItems
                            ? getTaskItemPosition({ editor, row: rows[insertionIndex] })
                            : undefined,
                    },
                };
            }

            // Dragging a full task list from here on
            if (!currentBoardId) return undefined;

            const sourceContent = asObject<TiptapContent>(getTextContent(item.element!));
            const transactionId = getNewTransactionId();

            // An Option-drag duplicates the source list's items into the target, so the source
            // element keeps its own items and is left on the board.
            if (isCopy) {
                const didCopy = copyTaskListItemsToTaskList({
                    sourceContent,
                    targetEditor: editor,
                    targetPosition: insertionPosition,
                    transactionId,
                });

                return didCopy ? { taskListElementDrop: true } : undefined;
            }

            const didMove = moveTaskListItemsToTaskList({
                sourceContent,
                targetEditor: editor,
                targetPosition: insertionPosition,
                transactionId,
            });

            if (!didMove) return undefined;

            dispatch(
                moveElementsToTrash({
                    elementId: getElementId(item.element!),
                    currentBoardId,
                    transactionId,
                }),
            );

            return { taskListElementDrop: true };
        },
        // `monitor.canDrop()` throws once a nested task-item target is unregistered mid-drag,
        // so the acceptance predicate is applied directly to the dragged item instead.
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: true }),
            canDrop: doesTaskListAcceptDrag({
                item: monitor.getItem(),
                editor,
                currentBoardId,
                targetElementId: elementId,
            }),
        }),
    });

    // Prevent an enclosing list or unsorted notes container from rendering its own indicator
    // while this task list is the shallow drop target that will handle the task-item drop.
    useEffect(() => {
        setParentHoveredChildAcceptsDrop?.(isOver && canDrop);
    }, [canDrop, isOver, setParentHoveredChildAcceptsDrop]);

    const connectDropTargetRef = useCallback(
        (element: HTMLDivElement | null) => {
            dropTargetElementRef.current = element;
            connectDropTarget(element);
        },
        [connectDropTarget],
    );

    return <>{children({ connectDropTarget: connectDropTargetRef, dropIndicator: isOver ? dropIndicator : null })}</>;
};

export default TaskListDropTarget;
